import { MOMENT_TYPES, isEolMoment } from './constants';
import { parseIsoDateOnlyParts } from './timelineDisplayDates';

/**
 * Subset of a moment/phase `generalInfo.dates` bag needed to resolve the primary
 * "release" date for the SLC approval subject. Kept loose (index signature) so it
 * stays compatible with the various moment-type date shapes the API returns.
 */
export interface SlcSubjectMomentDates {
  releaseDate?: string;
  releaseMonth?: string;
  releaseQuarter?: string;
  targetStart?: string;
  targetMonth?: string;
  targetQuarter?: string;
  effectiveDate?: string;
  effectiveMonth?: string;
  effectiveQuarter?: string;
  [key: string]: unknown;
}

/**
 * Moment-level context for building the subject. Sourced from the `generalInfo`
 * one level above the milestones section (the phase the milestone belongs to for
 * phased moments, or the moment root for top-level milestones).
 */
export interface SlcApprovalSubjectContext {
  /** Moment display name — the card name/title. */
  momentName?: string;
  /** API `momentType` (compare against {@link MOMENT_TYPES}). Drives which date field is the "release" date. */
  momentType?: string;
  /**
   * PCO flag for legacy "Offer, Price Change, or Program" moments. Retained as
   * moment context; no longer used for release-date resolution (both offer types
   * now route to the effective date by `momentType` alone).
   */
  pco?: boolean;
  /** `generalInfo.dates` for the relevant phase / moment. */
  dates?: SlcSubjectMomentDates;
}

/** Fallback token used for any subject part that has no usable value. */
export const SLC_SUBJECT_PLACEHOLDER = 'TBD';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
] as const;

const SPECIAL_DATE_VALUES = new Set(['TBD', 'N/A']);

/**
 * Formats a date value for the subject line.
 *
 * - `YYYY-MM-DD` ISO dates become `MMM D, YYYY` (e.g. `Jun 17, 2026`), parsed
 *   from the calendar parts so there is no timezone shift.
 * - Special statuses (`TBD` / `N/A`) pass through uppercased.
 * - Coarse labels the API already formats (e.g. `Q2 2026`, `Jun 2026`) pass
 *   through as-is.
 * - Empty / missing values fall back to {@link SLC_SUBJECT_PLACEHOLDER}.
 */
export function formatSlcSubjectDate(value?: string): string {
  const raw = (value || '').trim();
  if (!raw) return SLC_SUBJECT_PLACEHOLDER;

  const upper = raw.toUpperCase();
  if (SPECIAL_DATE_VALUES.has(upper)) return upper;

  const parts = parseIsoDateOnlyParts(raw);
  if (parts) {
    return `${MONTH_LABELS[parts.month - 1]} ${parts.day}, ${parts.year}`;
  }

  // Already-formatted coarse label (month/quarter) — keep as the API sent it.
  return raw;
}

/**
 * Resolves the moment's primary "release" date string, per moment type:
 *
 * - **Marketing Moment** → target date (`targetStart`).
 * - **Price Changes and Offers / Offer, Price Change, or Program / End of Life**
 *   → effective date (`effectiveDate`). Both offer types now carry an effective
 *   date, so this routes on `momentType` alone (no `pco` flag needed).
 * - **All other release-style moments** (Product Release with/without Marketing,
 *   New 1.0 Release, …) → release date (`releaseDate`).
 *
 * Each falls back to its coarser month/quarter field when the specific day is
 * absent. Returns an empty string when nothing is set (formatted later as
 * {@link SLC_SUBJECT_PLACEHOLDER}).
 */
export function resolveSlcReleaseDateValue(
  momentType: string | undefined,
  dates: SlcSubjectMomentDates | undefined
): string {
  const d = dates || {};

  if (momentType === MOMENT_TYPES.MARKETING_MOMENT) {
    return (d.targetStart || d.targetMonth || d.targetQuarter || '') as string;
  }

  if (
    momentType === MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS ||
    momentType === MOMENT_TYPES.PROMOS_OFFERS ||
    isEolMoment(momentType)
  ) {
    return (d.effectiveDate ||
      d.effectiveMonth ||
      d.effectiveQuarter ||
      '') as string;
  }

  return (d.releaseDate || d.releaseMonth || d.releaseQuarter || '') as string;
}

export interface BuildSlcApprovalSubjectInput
  extends SlcApprovalSubjectContext {
  /** Approval tier for the milestone's phase / moment (e.g. `L1`, `L2`). */
  tier?: string;
  /** Name of the milestone currently being edited. */
  milestoneName?: string;
  /** The milestone's Due Date (secondary date). ISO / coarse / special status. */
  dueDate?: string;
}

/**
 * Builds the readonly Send-for-Approval subject:
 *
 * `SLC APPROVAL REQUEST: <Tier> - <Release Date> - <Moment Name> - <Milestone Name> - Due <Due Date>`
 *
 * Every dynamic part falls back to {@link SLC_SUBJECT_PLACEHOLDER} when missing,
 * so the subject stays well-formed for any moment type / milestone shape.
 */
export function buildSlcApprovalSubject(
  input: BuildSlcApprovalSubjectInput
): string {
  const tier = (input.tier || '').trim() || SLC_SUBJECT_PLACEHOLDER;
  const releaseDate = formatSlcSubjectDate(
    resolveSlcReleaseDateValue(input.momentType, input.dates)
  );
  const momentName = (input.momentName || '').trim() || SLC_SUBJECT_PLACEHOLDER;
  const milestoneName =
    (input.milestoneName || '').trim() || SLC_SUBJECT_PLACEHOLDER;
  const dueDate = formatSlcSubjectDate(input.dueDate);

  return `SLC APPROVAL REQUEST: ${tier} - ${releaseDate} - ${momentName} - ${milestoneName} - Due ${dueDate}`;
}
